import { useContext } from 'react';
import {
  usePreferenceSearch,
  useMemberRead,
  useMemberReadv4,
} from '@sentara-health/sentara-api-hooks-core';
import { GlobalContext, isAvMed } from 'context';
import { mapProfileResponseToProfileInformations } from '../../pages/profile/mapProfileResponseToProfileInformations';
import { userAddressTypeHome, errorhandler } from 'common';

interface UseProfileAPIOptions {
  onSuccess?: (mappedProfile: any) => void;
  onError?: (error: any) => void;
}

export const usePreferenceSearchAPI = (options: UseProfileAPIOptions = {}) => {
  const { loginMemberId, setProfileState, profileState } =
    useContext(GlobalContext);
  const { fetchPreferenceSearch } = usePreferenceSearch();
  const { fetchMemberDetails } = useMemberRead();
  const { fetchMemberDetails: fetchMemberV4Details } = useMemberReadv4();

  const fetchProfile = async (options: UseProfileAPIOptions = {}) => {
    const searchData = {
      identifier: [
        {
          value: loginMemberId,
          system: 'memberId',
        },
      ],
    };
    const searchResponse = await fetchPreferenceSearch(searchData);
    try {
      if (searchResponse.data && Object.keys(searchResponse.data).length > 0) {
        const mappedProfile = mapProfileResponseToProfileInformations(
          searchResponse.data
        );

        const smartMergedProfile = smartMergeProfileData(
          profileState ?? {},
          mappedProfile
        );
        setProfileState(smartMergedProfile);
        if (options.onSuccess) {
          options.onSuccess(smartMergedProfile);
        }
      } else if (searchResponse?.errorCode) {
        errorhandler(searchResponse?.errorCode);
      }
    } catch (error) {
      console.error('Error fetching profile search:', error);
      if (options.onError) {
        options.onError(error);
      }
    }
  };

  const formatZipCode = (zipCode: string) => {
    console.log(zipCode, 'formatZipCode');
    if (zipCode && zipCode.length > 5) {
      return `${zipCode.slice(0, 5)}-${zipCode.slice(5)}`;
    }
    return zipCode || '';
  };

  const fetchV4MemberDetailsAPI = async () => {
    const v4memberData = {
      identifier: [
        {
          system: 'MemberId',
          value: loginMemberId,
        },
      ],
    };
    const memberV4Response = await fetchMemberV4Details(v4memberData);

    if (
      memberV4Response?.data?.address &&
      Array.isArray(memberV4Response.data.address)
    ) {
      // Find Home Address with type.Value = "0"
      const homeAddress = memberV4Response.data.address.find(
        (addr: any) => addr.type?.Value === '0'
      );

      // Transform V4 response to V2 format
      const addressResponseV2 = {
        address1: homeAddress?.line,
        city: homeAddress?.city,
        state: homeAddress?.state,
        zip: formatZipCode(homeAddress?.postalCode || ''),
      };
      return addressResponseV2;
    }
  };

  const fetchMemberDetailsAPI = async () => {
    let addressResponseV2: any = null;
    const memberData = { memberId: loginMemberId };
    if (isAvMed()) {
      // For AvMed: Use V4 API and map response to V2 format
      addressResponseV2 = await fetchV4MemberDetailsAPI();
    }
    const memberResponse = await fetchMemberDetails(memberData);

    try {
      if (memberResponse?.data?.name || memberResponse?.data?.address) {
        const addressResponse = isAvMed()
          ? addressResponseV2
          : memberResponse.data.address;

        setProfileState((prevState: any) => ({
          ...prevState,
          ...(memberResponse?.data?.name?.firstName && {
            firstName: memberResponse?.data.name.firstName,
          }),
          ...(memberResponse?.data?.name?.lastName && {
            lastName: memberResponse?.data.name.lastName,
          }),
          ...(addressResponse && {
            address: [
              {
                street: addressResponse?.address1,
                city: addressResponse?.city,
                state: addressResponse?.state,
                zipCode: addressResponse?.zip,
                addressType: userAddressTypeHome,
              },
            ],
          }),
        }));

        if (options.onSuccess) {
          options.onSuccess({
            firstName: memberResponse.data.name?.firstName,
            lastName: memberResponse.data.name?.lastName,
            address: addressResponse
              ? [
                  /*...*/
                ]
              : undefined,
          });
        }
      } else if (memberResponse?.errorCode) {
        errorhandler(memberResponse?.errorCode);
      }
    } catch (error) {
      console.error('Error fetching member details:', error);
      if (options.onError) {
        options.onError(error);
      }
    }
  };

  return {
    fetchProfile,
    fetchMemberDetailsAPI,
  };
};

export const smartMergeProfileData = (
  existingProfile: any,
  newProfileData: any
) => {
  // Define which fields should be protected from being overwritten with empty values
  const protectedFields = [
    'firstName',
    'lastName',
    'address',
  ];

  // Start with existing profile to preserve all existing data
  const mergedProfile = { ...existingProfile };

  Object.keys(newProfileData).forEach((key) => {
    const newValue = newProfileData[key];

    // Check if this is a protected field
    if (protectedFields.includes(key)) {
      // Only update protected fields if new value is valid (not empty/null/undefined)
      const isValidNewValue =
        newValue !== undefined &&
        newValue !== null &&
        newValue !== '' &&
        !(Array.isArray(newValue) && newValue.length === 0);

      if (isValidNewValue) {
        mergedProfile[key] = newValue;
      }
      // If new value is invalid, keep existing value (already preserved in mergedProfile)
    } else {
      // For non-protected fields, only update if the new value is not undefined
      // This prevents accidentally removing fields that aren't in the new data
      if (newValue !== undefined) {
        mergedProfile[key] = newValue;
      }
    }
  });

  return mergedProfile;
};
